import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Calendar, Clock, User, MapPin, Phone, Video, Check, 
  AlertCircle, ArrowLeft, ArrowRight, RefreshCw, Mail, ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const CandidateScheduling = () => {
  const { token } = useParams(); // Scheduling link token from URL
  const [searchParams] = useSearchParams();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [schedulingInfo, setSchedulingInfo] = useState({});
  const [candidateInfo, setCandidateInfo] = useState({
    name: '',
    email: '',
    phone: '',
    timezone: 'UTC',
    special_requirements: ''
  });
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Steps: 1=Select Time, 2=Enter Info, 3=Confirmation
  const steps = [
    { number: 1, title: 'Select Time', description: 'Choose your preferred interview time' },
    { number: 2, title: 'Your Information', description: 'Provide your contact details' },
    { number: 3, title: 'Confirmation', description: 'Review and confirm your booking' }
  ];

  useEffect(() => {
    if (token) {
      fetchAvailableSlots();
    } else {
      setError('Invalid scheduling link. Please contact the employer.');
    }
  }, [token]);

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/api/candidates/scheduling/${token}/slots`, {
        params: {
          timezone: candidateInfo.timezone || 'UTC'
        }
      });
      
      if (response.data.success) {
        setAvailableSlots(response.data.available_slots || []);
        setSchedulingInfo(response.data.scheduling_info || {});
        
        if (response.data.available_slots?.length === 0) {
          setError('No available time slots found. Please contact the employer to discuss alternative options.');
        }
      } else {
        setError('Unable to load available time slots. Please try again later.');
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      
      if (error.response?.status === 404) {
        setError('This scheduling link has expired or is no longer valid.');
      } else if (error.response?.status === 400) {
        setError('Maximum bookings reached for this position. Please contact the employer.');
      } else {
        setError('Unable to load available time slots. Please try again later.');
        
        // Fallback to mock data for development
        setAvailableSlots([
          {
            id: 'slot1',
            start_time: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            end_time: new Date(Date.now() + 25 * 60 * 60 * 1000).toISOString(),
            interviewer_name: 'Sarah Johnson',
            interviewer_id: 'interviewer_1',
            interview_type: 'screening',
            location: 'Video Call',
            is_available: true,
            timezone: 'UTC'
          },
          {
            id: 'slot2',
            start_time: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
            end_time: new Date(Date.now() + 49 * 60 * 60 * 1000).toISOString(),
            interviewer_name: 'Mike Chen',
            interviewer_id: 'interviewer_2',
            interview_type: 'screening',
            location: 'Phone Interview',
            is_available: true,
            timezone: 'UTC'
          }
        ]);
        setSchedulingInfo({
          job_id: 'job1',
          allowed_interview_types: ['screening'],
          instructions: 'Please select a time that works best for you.',
          bookings_remaining: 2
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelection = (slot) => {
    setSelectedSlot(slot);
    setCurrentStep(2);
  };

  const handleCandidateInfoSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!candidateInfo.name || !candidateInfo.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(candidateInfo.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setCurrentStep(3);
  };

  const handleBookingSubmit = async () => {
    try {
      setLoading(true);
      
      const bookingRequest = {
        slot_id: selectedSlot.id,
        selected_time: selectedSlot.start_time,
        interview_type: selectedSlot.interview_type,
        interviewer_id: selectedSlot.interviewer_id,
        candidate_name: candidateInfo.name,
        candidate_email: candidateInfo.email,
        candidate_phone: candidateInfo.phone,
        special_requirements: candidateInfo.special_requirements,
        timezone: candidateInfo.timezone
      };
      
      const response = await axios.post(`${API}/api/candidates/scheduling/${token}/book`, bookingRequest);
      
      if (response.data.success) {
        setBooking(response.data.booking);
        toast.success('Interview booked successfully!');
      } else {
        toast.error('Failed to book interview. Please try again.');
      }
    } catch (error) {
      console.error('Error booking interview:', error);
      
      if (error.response?.status === 404) {
        toast.error('This scheduling link has expired.');
      } else if (error.response?.status === 400) {
        toast.error('This time slot is no longer available. Please select another time.');
      } else {
        toast.error('Failed to book interview. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const getInterviewTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'phone': return <Phone className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getLocationIcon = (location) => {
    if (location?.toLowerCase().includes('video') || location?.toLowerCase().includes('meet')) {
      return <Video className="h-4 w-4 text-blue-600" />;
    } else if (location?.toLowerCase().includes('phone')) {
      return <Phone className="h-4 w-4 text-green-600" />;
    }
    return <MapPin className="h-4 w-4 text-gray-600" />;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">Unable to Load Scheduling</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={fetchAvailableSlots}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  if (booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <Check className="h-20 w-20 text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Interview Scheduled!</h1>
            <p className="text-gray-600">Your interview has been successfully booked.</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Details</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="font-medium">{formatDateTime(selectedSlot.start_time).date}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <span>{formatDateTime(selectedSlot.start_time).time} - {formatDateTime(selectedSlot.end_time).time}</span>
              </div>
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-blue-600" />
                <span>Interviewer: {selectedSlot.interviewer_name}</span>
              </div>
              <div className="flex items-center space-x-3">
                {getLocationIcon(selectedSlot.location)}
                <span>{selectedSlot.location}</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• You'll receive a confirmation email with all the details</li>
              <li>• A calendar invitation will be sent to your email</li>
              <li>• We'll send you reminders before the interview</li>
              <li>• Check your email for preparation instructions</li>
            </ul>
          </div>

          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Booking ID: <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{booking.id?.substring(0, 8)}</span>
            </p>
            <button 
              onClick={() => window.close()}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close Window
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-900">Schedule Your Interview</h1>
          <p className="text-gray-600 mt-2">Select a time that works best for you</p>
          
          {/* Progress Steps */}
          <div className="mt-6">
            <div className="flex items-center">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      currentStep >= step.number 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {currentStep > step.number ? <Check className="h-4 w-4" /> : step.number}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${currentStep >= step.number ? 'text-blue-600' : 'text-gray-500'}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-px mx-4 ${currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Step 1: Select Time */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Available Interview Times</h2>
            
            {schedulingInfo.instructions && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-800">{schedulingInfo.instructions}</p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-3 animate-spin" />
                <p className="text-gray-600">Loading available times...</p>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableSlots.map((slot) => {
                  const { date, time } = formatDateTime(slot.start_time);
                  const endTime = formatDateTime(slot.end_time).time;
                  
                  return (
                    <div 
                      key={slot.id}
                      onClick={() => handleSlotSelection(slot)}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          {getInterviewTypeIcon(slot.interview_type)}
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {slot.interview_type} Interview
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">Available</span>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">{date}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span>{time} - {endTime}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span>{slot.interviewer_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getLocationIcon(slot.location)}
                          <span>{slot.location}</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <button className="w-full px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors">
                          Select This Time
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Available Times</h3>
                <p className="text-gray-600">Please contact the employer to discuss alternative scheduling options.</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Candidate Information */}
        {currentStep === 2 && selectedSlot && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Your Information</h2>
              <button 
                onClick={() => setCurrentStep(1)}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Time Selection</span>
              </button>
            </div>

            {/* Selected Time Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Selected Interview Time</h3>
              <div className="flex items-center space-x-4 text-sm">
                <span>{formatDateTime(selectedSlot.start_time).date}</span>
                <span>{formatDateTime(selectedSlot.start_time).time} - {formatDateTime(selectedSlot.end_time).time}</span>
                <span>with {selectedSlot.interviewer_name}</span>
              </div>
            </div>

            <form onSubmit={handleCandidateInfoSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={candidateInfo.name}
                    onChange={(e) => setCandidateInfo(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={candidateInfo.email}
                    onChange={(e) => setCandidateInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={candidateInfo.phone}
                    onChange={(e) => setCandidateInfo(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <select
                    value={candidateInfo.timezone}
                    onChange={(e) => setCandidateInfo(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="Europe/London">London</option>
                    <option value="Europe/Paris">Paris</option>
                    <option value="Asia/Tokyo">Tokyo</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Special Requirements or Accommodations
                </label>
                <textarea
                  value={candidateInfo.special_requirements}
                  onChange={(e) => setCandidateInfo(prev => ({ ...prev, special_requirements: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Any accessibility needs or special requirements..."
                />
              </div>

              <div className="flex justify-end">
                <button 
                  type="submit"
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <span>Continue to Review</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {currentStep === 3 && selectedSlot && candidateInfo.name && (
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Review & Confirm</h2>
              <button 
                onClick={() => setCurrentStep(2)}
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Information</span>
              </button>
            </div>

            <div className="space-y-6">
              {/* Interview Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Interview Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{formatDateTime(selectedSlot.start_time).date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>{formatDateTime(selectedSlot.start_time).time} - {formatDateTime(selectedSlot.end_time).time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>Interviewer: {selectedSlot.interviewer_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getLocationIcon(selectedSlot.location)}
                    <span>{selectedSlot.location}</span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Your Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>{candidateInfo.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{candidateInfo.email}</span>
                  </div>
                  {candidateInfo.phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span>{candidateInfo.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>Timezone: {candidateInfo.timezone}</span>
                  </div>
                </div>
                {candidateInfo.special_requirements && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700">Special Requirements:</p>
                    <p className="text-sm text-gray-600">{candidateInfo.special_requirements}</p>
                  </div>
                )}
              </div>

              {/* Confirmation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">By confirming this interview:</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li>• You'll receive email confirmation and calendar invitation</li>
                  <li>• We'll send you reminder notifications</li>
                  <li>• You can reschedule or cancel if needed</li>
                  <li>• Please arrive 5 minutes before the scheduled time</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-4">
                <button 
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Back to Edit
                </button>
                <button 
                  onClick={handleBookingSubmit}
                  disabled={loading}
                  className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  <span>Confirm Interview</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateScheduling;